import { supabase } from "../../lib/supabase";
import type { CreateJobInput, CreateJobProgress, DownloadKind, JobFilters, SvgJob } from "../../lib/types";
import { isActive } from "./status";

async function functionError(error: unknown, fallback: string): Promise<Error> {
  if (error && typeof error === "object" && "context" in error) {
    const context = (error as { context?: Response }).context;
    if (context instanceof Response) {
      try {
        const body = await context.clone().json() as { error?: string };
        if (body.error) return new Error(body.error);
      } catch {
        try {
          const text = await context.clone().text();
          if (text) return new Error(text);
        } catch {
          // Ignore unreadable error bodies.
        }
      }
    }
  }
  return error instanceof Error ? error : new Error(fallback);
}

export async function listSvgJobs(filters: JobFilters): Promise<SvgJob[]> {
  const { data, error } = await supabase.from("svg_jobs").select("*").order("created_at", { ascending: false }).limit(100);
  if (error) throw error;
  const search = filters.search.trim().toLocaleLowerCase();
  return (data as SvgJob[]).filter((job) => {
    if (filters.status === "active" && !isActive(job)) return false;
    if (filters.status === "completed" && job.status !== "completed") return false;
    if (filters.status === "failed" && !["failed", "cancelled", "expired"].includes(job.status)) return false;
    if (filters.status === "favorite" && !job.is_favorite) return false;
    if (!search) return true;
    return `${job.source_filename} ${job.engine} ${job.progress_stage}`.toLocaleLowerCase().includes(search);
  });
}

export async function getSvgJob(jobId: string): Promise<SvgJob> {
  const { data, error } = await supabase.from("svg_jobs").select("*").eq("id", jobId).single();
  if (error) throw error;
  return data as SvgJob;
}

export async function createSvgJob(input: CreateJobInput, onProgress?: (progress: CreateJobProgress) => void): Promise<string> {
  onProgress?.({ stage: "creating", percent: 8 });
  const { data: created, error: createError } = await supabase.functions.invoke("create-svg-job", {
    body: {
      engine: input.engine,
      sourceKind: input.sourceKind,
      filename: input.source.name,
      sourceSize: input.source.size,
      optimize: input.optimize,
    },
  });
  if (createError) throw await functionError(createError, "无法创建任务");
  const payload = created as { jobId: string; bucket: string; sourcePath: string };

  onProgress?.({ stage: "uploading", percent: 16 });
  const { error: uploadError } = await supabase.storage.from(payload.bucket).upload(payload.sourcePath, input.source, {
    upsert: false,
    contentType: input.source.type || "application/octet-stream",
  });
  if (uploadError) throw uploadError;

  onProgress?.({ stage: "queueing", percent: 24 });
  const { error: startError } = await supabase.functions.invoke("start-svg-job", { body: { jobId: payload.jobId } });
  if (startError) throw await functionError(startError, "无法启动构建任务");
  return payload.jobId;
}

export async function requestJobFile(jobId: string, kind: DownloadKind): Promise<string> {
  const { data, error } = await supabase.functions.invoke("get-svg-download", { body: { jobId, kind } });
  if (error) throw await functionError(error, "无法获取文件链接");
  const url = (data as { url?: string }).url;
  if (!url) throw new Error("服务未返回文件链接");
  return url;
}

export async function manageSvgJob(jobId: string, action: "favorite" | "cancel" | "retry" | "delete", options: { favorite?: boolean } = {}) {
  const { data, error } = await supabase.functions.invoke("manage-svg-job", { body: { jobId, action, ...options } });
  if (error) throw await functionError(error, "任务操作失败");
  return data as { job?: SvgJob; jobId?: string; deleted?: boolean };
}
